import { useEffect, useState } from "react";
import type { ClarificationRequestV1 } from "@enrein/shared-contracts";
import { toClientApiErrorMessage } from "@enrein/shared-contracts/display/tier1-client-display";

export type ClientClarificationApi = {
  readonly listClarifications: () => Promise<{ items: ClarificationRequestV1[] }>;
  readonly respondToClarification: (
    clarificationRequestId: string,
    body: {
      schemaVersion: "clarification-respond.v1.0";
      responseText: string;
      expectedLifecycleVersion: number;
    },
  ) => Promise<{
    ok: boolean;
    error?: string;
    nextAction?: string;
    request?: ClarificationRequestV1;
  }>;
};

export type ClarificationWorkspaceProps = {
  readonly api: ClientClarificationApi;
};

/**
 * Block 1 Client Workspace clarification journey.
 * Client responses create candidate evidence only — never activate live site truth.
 */
export function ClarificationWorkspace(props: ClarificationWorkspaceProps) {
  const { api } = props;
  const [items, setItems] = useState<ClarificationRequestV1[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextAction, setNextAction] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listClarifications();
      setItems(list.items);
      if (list.items.length === 0) {
        setNextAction("No open information requests.");
      }
    } catch (err) {
      const code = err instanceof Error ? err.message : String(err);
      const mapped = toClientApiErrorMessage(code);
      setError(mapped);
      const sessionLost =
        /session|missing_token|unauthorized|permission|access|internal_auth/i.test(code) ||
        /session|permission|access/i.test(mapped);
      setNextAction(
        sessionLost
          ? "This client link session expired. Ask your agency for a new copy-link and open it again."
          : null,
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function onSubmit(item: ClarificationRequestV1) {
    const text = (drafts[item.clarificationRequestId] ?? "").trim();
    if (text.length === 0) {
      setError("validation_failed");
      setNextAction("Enter a response before submitting.");
      return;
    }
    setBusyId(item.clarificationRequestId);
    setError(null);
    try {
      const result = await api.respondToClarification(item.clarificationRequestId, {
        schemaVersion: "clarification-respond.v1.0",
        responseText: text,
        expectedLifecycleVersion: item.lifecycleVersion,
      });
      if (!result.ok) {
        setError(result.error ?? "request_failed");
        setNextAction(result.nextAction ?? "Reload and try again.");
        return;
      }
      setNextAction(
        result.nextAction ??
          "Submitted as candidate evidence. Your agency must review — this does not activate site truth.",
      );
      await reload();
    } catch (err) {
      const code = err instanceof Error ? err.message : String(err);
      setError(toClientApiErrorMessage(code));
      setNextAction("Your response was not recorded. Retry.");
    } finally {
      setBusyId(null);
    }
  }

  const openItems = items.filter((i) => i.status === "OPEN" || i.status === "PENDING_REVIEW");

  return (
    <section aria-label="Client clarification workspace" data-testid="client-clarification-workspace">
      <h2>Information requests</h2>
      <p>Respond to missing or uncertain information. Replies are review evidence only.</p>

      {loading ? <p>Loading requests…</p> : null}
      {!loading && !error && openItems.length === 0 ? <p>No open requests.</p> : null}
      {error ? (
        <p role="alert" data-testid="client-clarification-error">
          {error}
        </p>
      ) : null}
      {nextAction ? <p data-testid="client-clarification-next-action">{nextAction}</p> : null}

      <ul>
        {openItems.map((item) => (
          <li key={item.clarificationRequestId}>
            <p>
              <strong>{item.kind}</strong>: {item.prompt}
            </p>
            <label htmlFor={`clr-response-${item.clarificationRequestId}`}>Your response</label>
            <textarea
              id={`clr-response-${item.clarificationRequestId}`}
              value={drafts[item.clarificationRequestId] ?? ""}
              onChange={(e) =>
                setDrafts((prev) => ({ ...prev, [item.clarificationRequestId]: e.target.value }))
              }
              disabled={busyId === item.clarificationRequestId}
            />
            <button
              type="button"
              data-testid="client-submit-clarification-response"
              data-clarification-id={item.clarificationRequestId}
              onClick={() => void onSubmit(item)}
              disabled={busyId === item.clarificationRequestId}
            >
              Submit response
            </button>
          </li>
        ))}
      </ul>

      <h3>Submitted / resolved</h3>
      <ul>
        {items
          .filter((i) => i.status === "SUBMITTED" || i.status === "RESOLVED" || i.status === "REJECTED")
          .map((item) => (
            <li key={item.clarificationRequestId}>
              {item.status}: {item.prompt}
              {item.responseText ? ` — ${item.responseText}` : ""}
            </li>
          ))}
      </ul>
    </section>
  );
}
